import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/ui/Sidebar';
import UserAccountMenu from '../../components/ui/UserAccountMenu';


import { useNavigate } from 'react-router-dom';
import MetricsCard from './components/MetricsCard';
import QuickActionsGrid from './components/QuickActionsGrid';
import ActivityFeed from './components/ActivityFeed';
import PendingTasksCard from './components/PendingTasksCard';
import SystemStatusCard from './components/SystemStatusCard';
import AssetManagementPanel from './components/AssetManagementPanel';
import DashboardContentPanel from './components/DashboardContentPanel';
import CalendlyAvailabilityPanel from './components/CalendlyAvailabilityPanel';
import AssetUploadModal from './components/AssetUploadModal';
import { fetchDashboardMetrics, fetchRecentActivity } from '../../services/adminDashboardService';


const AdminDashboard = () => {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState([
    {
      id: 1,
      label: 'Total Users',
      value: '0',
      icon: 'Users',
      change: '+0 this month',
      changeType: 'neutral',
      trend: '0%'
    },
    {
      id: 2,
      label: 'Active Providers',
      value: '0',
      icon: 'UserCheck',
      change: '+0 this week',
      changeType: 'neutral',
      trend: '0%'
    },
    {
      id: 3,
      label: 'Total Assets',
      value: '0',
      icon: 'FolderOpen',
      change: '+0 this week',
      changeType: 'neutral',
      trend: 'Loading'
    },
    {
      id: 4,
      label: 'System Activity',
      value: '0',
      icon: 'Activity',
      change: 'Last 24 hours',
      changeType: 'neutral',
      trend: 'Normal'
    }
  ]);

  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  const [pendingTasks, setPendingTasks] = useState([
    {
      id: 1,
      title: 'Review new user registrations',
      count: 8,
      priority: 'high',
      action: () => navigate('/user-management')
    },
    {
      id: 2,
      title: 'Review flagged assets',
      count: 3,
      priority: 'low',
      action: () => console.log('Navigate to asset review')
    }
  ]);

  const [showUploadModal, setShowUploadModal] = useState(false);

  // Fetch dashboard data on mount
  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch metrics
      const metricsData = await fetchDashboardMetrics();
      
      // Calculate percentage changes
      const userChangePercent = metricsData?.totalUsers > 0 
        ? ((metricsData?.newUsersThisMonth / metricsData?.totalUsers) * 100)?.toFixed(1)
        : '0';
      
      const assetChangePercent = metricsData?.totalAssets > 0
        ? ((metricsData?.newAssetsThisWeek / metricsData?.totalAssets) * 100)?.toFixed(1)
        : '0';

      // Update metrics with real data
      setMetrics([
        {
          id: 1,
          label: 'Total Users',
          value: metricsData?.totalUsers?.toString() || '0',
          icon: 'Users',
          change: `+${metricsData?.newUsersThisMonth || 0} this month`,
          changeType: metricsData?.newUsersThisMonth > 0 ? 'increase' : 'neutral',
          trend: `+${userChangePercent}%`
        },
        {
          id: 2,
          label: 'Active Providers',
          value: metricsData?.activeProviders?.toString() || '0',
          icon: 'UserCheck',
          change: 'Users with activity',
          changeType: 'neutral',
          trend: 'Active'
        },
        {
          id: 3,
          label: 'Total Assets',
          value: metricsData?.totalAssets?.toString() || '0',
          icon: 'FolderOpen',
          change: `+${metricsData?.newAssetsThisWeek || 0} this week`,
          changeType: metricsData?.newAssetsThisWeek > 0 ? 'increase' : 'neutral',
          trend: assetChangePercent > 0 ? `+${assetChangePercent}%` : 'Stable'
        },
        {
          id: 4,
          label: 'System Activity',
          value: metricsData?.systemActivity?.toString() || '0',
          icon: 'Activity',
          change: 'Last 24 hours',
          changeType: 'neutral',
          trend: metricsData?.systemActivity > 10 ? 'High' : 'Normal'
        }
      ]);

      // Fetch recent activity
      const activityData = await fetchRecentActivity();
      setRecentActivity(activityData);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadClick = () => {
    setShowUploadModal(true);
  };

  const handleUploadSuccess = () => {
    setShowUploadModal(false);
    // Refresh dashboard data after upload
    loadDashboardData();
  };

  return (
    <div className="flex h-screen bg-background">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-card border-b border-border px-8 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Admin Dashboard</h1>
            <p className="text-sm text-muted-foreground mt-1">System overview and management</p>
          </div>
          <UserAccountMenu />
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto px-8 py-6">
          {/* Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {metrics?.map((metric) => (
              <MetricsCard key={metric?.id} metric={metric} />
            ))}
          </div>

          {/* Quick Actions */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">Quick Actions</h2>
            <QuickActionsGrid onUploadClick={handleUploadClick} />
          </div>

          {/* Calendly Availability Panel */}
          <div className="mb-8">
            <CalendlyAvailabilityPanel />
          </div>

          {/* Dashboard Content Panel */}
          <div className="mb-8">
            <DashboardContentPanel />
          </div>

          {/* Asset Management Panel */}
          <div className="mb-8">
            <AssetManagementPanel 
              showUploadModal={showUploadModal}
              onUploadClick={handleUploadClick}
              onCloseUploadModal={() => setShowUploadModal(false)}
            />
          </div>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            {/* Activity Feed - Takes 2 columns */}
            <div className="lg:col-span-2">
              {loading ? (
                <div className="bg-card border border-border rounded-lg p-8 text-center">
                  <p className="text-muted-foreground">Loading activity...</p>
                </div>
              ) : (
                <ActivityFeed activities={recentActivity} />
              )}
            </div>

            {/* Side Column */}
            <div className="space-y-6">
              <PendingTasksCard tasks={pendingTasks} />
              <SystemStatusCard />
            </div>
          </div>
        </main>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <AssetUploadModal
          onClose={() => setShowUploadModal(false)}
          onSuccess={handleUploadSuccess}
        />
      )}
    </div>
  );
};

export default AdminDashboard;